<template>
  <v-container fluid>
    <v-card class="pa-5 mt-3" :loading="loading" v-if="category">
      <v-card-title>
        {{ category.course.title }} - {{ category.stageTitle }} - {{ category.name }}
      </v-card-title>
      <v-toolbar flat>
        <v-text-field
            v-if="!reorderMode"
            v-model="search"
            append-icon="mdi-magnify"
            outlined
            dense
            :label="$t('search')"
            single-line
            hide-details
        ></v-text-field>
        <v-spacer></v-spacer>

        <v-menu
            v-if="selectedItems.length > 0 && !reorderMode"
            offset-y
            :close-on-content-click="false"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
                color="primary"
                dark
                class="mb-2 mr-2"
                v-bind="attrs"
                v-on="on"
            >
              <v-icon left>mdi-menu</v-icon>
              {{ $t("actions") }} ({{ selectedItems.length }})
              <v-icon right small>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="enableSelectedItems">
              <v-list-item-icon>
                <v-icon color="success">mdi-check-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t("enable") }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item @click="disableSelectedItems">
              <v-list-item-icon>
                <v-icon color="warning">mdi-cancel</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t("disable") }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-divider></v-divider>

            <v-list-item @click="deleteSelectedItems">
              <v-list-item-icon>
                <v-icon color="error">mdi-delete</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t("delete") }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn
            v-if="$store.getters.isInRole(110) && !reorderMode"
            color="secondary"
            dark
            class="mb-2 ms-2"
            @click="openPlaylistDialog"
        >
          {{ $t("addPlayListLink") }}
          <v-icon class="mx-2">mdi-youtube</v-icon>
        </v-btn>

        <v-btn
            v-if="$store.getters.isInRole(111) && !reorderMode"
            color="warning"
            dark
            class="mb-2  mx-2 mr-2"
            @click="toggleReorderMode"
        >
          <v-icon left>mdi-sort</v-icon>
          {{ $t("Reorder Items") }}
        </v-btn>

        <v-btn
            v-if="reorderMode"
            color="success"
            dark
            class="mb-2 mr-2"
            @click="saveOrder"
            :loading="savingOrder"
        >
          <v-icon left>mdi-content-save</v-icon>
          {{ $t("Save Order") }}
        </v-btn>

        <v-btn
            v-if="reorderMode"
            color="error"
            dark
            class="mb-2 mr-2"
            @click="cancelReorder"
        >
          <v-icon left>mdi-close</v-icon>
          {{ $t("Cancel") }}
        </v-btn>

        <v-btn
            v-if="$store.getters.isInRole(110) && !reorderMode"
            color="primary"
            dark
            class="mb-2"
            @click="dialog = true"
        >
          {{ $t("add") }}
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-toolbar>

      <data-table-component
          v-if="!reorderMode"
          :headers="headers"
          :items="items"
          :search="search"
          :loading="loading"
          :show-select="true"
          v-model="selectedItems"
          default-items-per-page="-1"
          @selection-changed="onSelectionChanged"
      />

      <div v-else>
        <v-alert type="info" class="mb-4">
          اسحب العناصر لإعادة ترتيبها، ثم انقر فوق "حفظ الترتيب" لتطبيق التغييرات.
        </v-alert>

        <draggable
            v-model="reorderItems"
            :animation="200"
            handle=".handle"
            @start="drag=true"
            @end="drag=false"
        >
          <transition-group type="transition" name="flip-list">
            <v-card
                v-for="(item, index) in reorderItems"
                :key="item.id"
                class="mb-2 pa-3 d-flex align-center"
                outlined
            >
              <v-icon class="handle mr-3" style="cursor: move;">
                mdi-drag-vertical
              </v-icon>

              <div class="d-flex align-center flex-grow-1">
                <v-chip class="me-3" small label>
                  #{{ index + 1 }}
                </v-chip>

                <div class="flex-grow-1">
                  <div class="font-weight-bold">{{ item.title }}</div>
                  <div class="text-caption grey--text">
                    التسلسل الاصلي : {{ item.order }}
                  </div>
                </div>

                <v-chip
                    v-if="item.order !== index + 1"
                    color="warning"
                    small
                    label
                    outlined
                    class="ml-3"
                >
                  تم التغيير
                  <v-icon small class="ms-2">mdi-reorder-horizontal</v-icon>
                </v-chip>
              </div>
            </v-card>
          </transition-group>
        </draggable>
      </div>
    </v-card>

    <!-- Existing Dialogs -->
    <Dialog
        :onClose="close"
        :dialog="dialog"
        :editedItem="editedItem"
        :editedIndex="editedIndex"
        :valid="valid"
        :category="category"
        :resetValidation="resetValidation"
        @refreshTable="refreshTable"
    />

    <PlaylistDialog
        :open-dialog="playListDialog"
        :category="category"
        @close="closePlaylistDialog"
        @refresh="refreshTable"
    />

    <confirm-dialog
        :openDialog="dialogDelete"
        :onClicked="deleteItemConfirm"
        :onClose="closeDelete"
    />
    <confirm-dialog
        dialog-title="تفعيل الدروس المحددة"
        :openDialog="dialogEnableMultiple"
        :onClicked="enableMultipleConfirm"
        :onClose="closeEnableMultiple"
        tool-bar-color="success"
        :dialog-text="$t('enableMultipleConfirm')"
        :message="'هل أنت متأكد من تفعيل ' + selectedItems.length + ' عنصر؟'"
    />
    <confirm-dialog
        dialog-title="تعطيل الدروس المحددة"
        :openDialog="dialogDisableMultiple"
        :onClicked="disableMultipleConfirm"
        :onClose="closeDisableMultiple"
        tool-bar-color="error"
        :dialog-text="$t('disableMultipleConfirm')"
        :message="'هل أنت متأكد من تعطيل ' + selectedItems.length + ' عنصر؟'"
    />
    <confirm-dialog
        dialog-title="حذف الدروس المحددة"
        :openDialog="dialogDeleteMultiple"
        :onClicked="deleteMultipleConfirm"
        :onClose="closeDeleteMultiple"
        tool-bar-color="error"
        :dialog-text="$t('deleteMultipleConfirm')"
        :message="'هل أنت متأكد من حذف ' + selectedItems.length + ' عنصر؟ لا يمكن التراجع عن هذا الإجراء.'"
    />
  </v-container>
</template>

<script>
import draggable from 'vuedraggable';
import ConfirmDialog from "../../../components/ConfirmDialog.vue";
import Dialog from "./Dialog.vue";
import PlaylistDialog from "./PlaylistDialog.vue";
import DataTableComponent from "../../../components/DataTableComponent.vue";

export default {
  components: {
    DataTableComponent,
    ConfirmDialog,
    Dialog,
    PlaylistDialog,
    draggable
  },
  name: "VideoLibraryVideos",
  props: [
    "category"
  ],
  data() {
    return {
      loading: true,
      valid: true,
      playListDialog: false,
      dialog: false,
      dialogDelete: false,
      dialogEnableMultiple: false,
      dialogDisableMultiple: false,
      dialogDeleteMultiple: false,
      editedIndex: -1,
      search: "",
      items: [],
      selectedItems: [],
      reorderMode: false,
      reorderItems: [],
      originalOrder: [],
      savingOrder: false,
      drag: false,
      editedItem: {
        id: 0,
        title: null,
        order: 0,
        link: null,
        details: null,
        active: false,
        duration: 0,
      },
      specializations: [
        {
          text: this.$t("specializations.educational"),
          value: "educational",
        },
        {
          text: this.$t("specializations.specialty"),
          value: "specialty",
        },
      ],
      defaultItem: {
        id: 0,
        title: null,
        order: 0,
        link: null,
        details: null,
        active: false,
        image: null,
        imagePreview: null,
        image_url: null
      },
      resetValidation: 0,
    };
  },
  computed: {
    headers() {
      var list = [
        {text: this.$t("videoLibrary.videos.order"), value: "order"},
        {text: this.$t("videoLibrary.videos.name"), value: "title"},
        {text: this.$t("videoLibrary.videos.link"), value: "link"},
        {
          text: this.$t("videoLibrary.videos.details"),
          value: "details",
          map: (i) => i ? i.substring(0, 24) + "..." : ''
        },
        {
          text: this.$t("active"),
          value: "active",
          type: "checkbox",
          readonly: true,
        },
      ];
      var actions = {
        text: "",
        value: "actions",
        templates: [],
      };
      if (this.$store.getters.isInRole(111)) {
        actions.templates.push({
          type: "btn",
          icon: "mdi-pencil",
          text: this.$t("edit"),
          click: this.editItem,
        });
      }
      if (this.$store.getters.isInRole(112)) {
        actions.templates.push({
          type: "btn",
          icon: "mdi-delete",
          text: this.$t("delete"),
          click: this.deleteItem,
          color: "red darken-2",
        });
      }
      list.push(actions);
      return list;
    },
  },
  created() {
    if (!this.$store.getters.isInRole(109)) {
      this.$router.push({name: "notauthorize"});
    }
    this.loading = true;
    if (this.category)
      this.refreshTable();
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    category() {
      this.refreshTable();
    }
  },
  methods: {
    openPlaylistDialog() {
      this.playListDialog = true;
    },

    closePlaylistDialog() {
      this.playListDialog = false;
    },

    onSelectionChanged(selected) {
      this.selectedItems = selected;
    },

    enableSelectedItems() {
      if (this.selectedItems.length > 0) {
        this.dialogEnableMultiple = true;
      }
    },

    disableSelectedItems() {
      if (this.selectedItems.length > 0) {
        this.dialogDisableMultiple = true;
      }
    },

    deleteSelectedItems() {
      if (this.selectedItems.length > 0) {
        this.dialogDeleteMultiple = true;
      }
    },

    async enableMultipleConfirm() {
      try {
        const form = new FormData();
        this.selectedItems.forEach((item) => {
          form.append('ids[]', item.id);
        })
        await this.$axios.post('video_library/videos/enable/bulk', form);
        this.selectedItems = [];
        this.refreshTable();
      } catch (error) {
        this.$toast.error('حدث خطأ أثناء تفعيل العناصر');
      }
      this.closeEnableMultiple();
    },

    async disableMultipleConfirm() {
      try {
        const form = new FormData();
        this.selectedItems.forEach((item) => {
          form.append('ids[]', item.id);
        })
        await this.$axios.post('video_library/videos/disable/bulk', form);
        this.selectedItems = [];
        this.refreshTable();
      } catch (error) {
        this.$toast.error('حدث خطأ أثناء تعطيل العناصر');
      }
      this.closeDisableMultiple();
    },

    async deleteMultipleConfirm() {
      try {
        const form = new FormData();
        this.selectedItems.forEach((item) => {
          form.append('ids[]', item.id);
        })
        await this.$axios.post('video_library/videos/delete/bulk', form);
        this.selectedItems = [];
        this.refreshTable();
        this.$toast.success('تم حذف العناصر بنجاح');
      } catch (error) {
        this.$toast.error('حدث خطأ أثناء حذف العناصر');
      }
      this.closeDeleteMultiple();
    },

    closeEnableMultiple() {
      this.dialogEnableMultiple = false;
    },

    closeDisableMultiple() {
      this.dialogDisableMultiple = false;
    },

    closeDeleteMultiple() {
      this.dialogDeleteMultiple = false;
    },

    toggleReorderMode() {
      this.reorderMode = true;
      this.reorderItems = [...this.items].sort((a, b) => a.order - b.order);
      this.originalOrder = [...this.reorderItems];
      this.selectedItems = [];
    },

    cancelReorder() {
      this.reorderMode = false;
      this.reorderItems = [];
      this.originalOrder = [];
    },

    async saveOrder() {
      this.savingOrder = true;

      try {
        const orderData = this.reorderItems.map((item, index) => ({
          id: item.id,
          order: index + 1
        }));

        const response = await this.$axios.post('video_library/videos/reorder', {
          items: orderData
        });

        if (response.data.status === 'Successful') {
          this.items = this.items.map(item => {
            const reorderedItem = this.reorderItems.find(r => r.id === item.id);
            const newIndex = this.reorderItems.indexOf(reorderedItem);
            return {
              ...item,
              order: newIndex + 1
            };
          });

          this.reorderMode = false;
          this.reorderItems = [];
          this.originalOrder = [];

          this.refreshTable();
        }
      } catch (error) {
        this.$toast.error('هنالك خطأ الرجاء اعادة المحاولة');
      } finally {
        this.savingOrder = false;
      }
    },

    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      var deleteItem = this.items[this.editedIndex];
      this.$axios
          .delete("video_library/videos/" + deleteItem.id)
          .then(() => {
            this.refreshTable();
          });

      this.closeDelete();
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
      this.resetValidation += 1;
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    refreshTable() {
      this.$axios
          .get("video_library/videos?category_id=" + this.category.id, {failureToast: true})
          .then((response) => {
            this.items = response.data.data;
            this.selectedItems = [];
          })
          .finally(() => {
            this.loading = false;
          });
    },
  },
};
</script>
